import { useEffect, useState, lazy, Suspense } from 'react'
import { AppRouter } from './app/router/AppRouter'
import { normalisePath } from './app/router/routes'
import { useApplyReduceMotion } from './shared/hooks/useReduceMotion'

const DevIndex = lazy(() => import('./dev/DevIndex'))

/**
 * The application root: one branch, between the product and the dev harnesses.
 *
 * Every hash renders `AppRouter` — in development exactly as in production, so
 * `npm run dev` opens the thing being built rather than a workbench for it.
 *
 * `#/dev` is the single exception, and only in a development build. The
 * specimen sheets and the camera · pose · calibration · game harnesses are not
 * routes (a four-year-old never navigates), so they need somewhere to live, and
 * behind a hash they are one keystroke away without standing in front of the app.
 */
export default function App() {
  // Resolves the OS preference and the in-app setting, and stamps
  // data-reduce-motion on the root. No component branches on it.
  useApplyReduceMotion()

  // Called unconditionally — the branch below is on its VALUE, never on whether
  // the hook runs, or the hook order changes between renders.
  const harness = useIsHarnessHash()

  /*
   * ⚠️ `DevIndex` IS BEHIND `#/dev`, NOT THE DEFAULT SCREEN.
   *
   * It used to win outright in development, which meant `npm run dev` opened the
   * specimen switcher and the actual product was unreachable without a
   * production build — the same class of mistake OD-89 fixed one layer down,
   * where four units of screens sat mounted nowhere.
   *
   * It is still the only way to reach the camera, pose, calibration and game
   * harnesses, and none of them is a route because a four-year-old never
   * navigates. So it stays — one hash away, and stripped from the production
   * bundle by the `import.meta.env.DEV` guard, which Vite folds to `false` at
   * build time so the tree-shaker drops `DevIndex` and everything it imports.
   */
  if (import.meta.env.DEV && harness) {
    return (
      <Suspense fallback={null}>
        <DevIndex />
      </Suspense>
    )
  }

  /*
   * ✅ OD-89 — THE LINE THAT MADE FOUR UNITS OF SCREENS REACHABLE.
   *
   * This returned *"Unit 1.2 · shells — Components begin in §1.3"* in production
   * while the teacher tier (§8.4), the parent tier (§9.4), the school tier and
   * rollover (§10.1) and the feedback modal (§10.3) sat written, tested,
   * bilingual and calling real endpoints — mounted nowhere. The production
   * bundle had been 173 modules for four units, because the tree-shaker dropped
   * every one of them.
   *
   * 🔴 The router is ~150 lines rather than a dependency: ADR-034 removed
   * `react-router-dom` for GHSA-qwww-vcr4-c8h2 and named no replacement, and
   * what this product needs — parse a hash, match a table, render — is small
   * enough that taking a new dependency on the path that gates every screen was
   * the worse trade.
   */
  return <AppRouter />
}

/**
 * Is the hash `#/dev`?
 *
 * ⚠️ NOT A `RouteId`, AND DELIBERATELY NOT IN `ROUTES`. That table is the
 * product's navigation — `navFor` maps over it to build the sidebar, and a dev
 * harness listed there would be a nav item a school could click. This is a
 * separate, dev-only check that the route table never learns about.
 *
 * Subscribes to `hashchange` for the same reason `useRoute` does: it is a real
 * browser event, so back and forward between `#/dev` and the app work without a
 * history stack to maintain.
 */
function useIsHarnessHash(): boolean {
  const [harness, setHarness] = useState(() => isHarness())

  useEffect(() => {
    const onChange = (): void => {
      setHarness(isHarness())
    }
    window.addEventListener('hashchange', onChange)
    // Re-read on mount too — the hash may already be set by a bookmark or a
    // refresh, and a listener alone would render the app over it.
    onChange()
    return () => {
      window.removeEventListener('hashchange', onChange)
    }
  }, [])

  return harness
}

function isHarness(): boolean {
  /*
   * ⚠️ `dev` OR ANYTHING UNDER IT. The harness switcher now names its view in
   * the hash (`#/dev/scenes`) so a harness can be linked to rather than
   * described — "open dev, then click the fifth tab". Matching `dev` exactly
   * sent every such link to the marketing home page instead.
   */
  const path = normalisePath(window.location.hash)
  return path === 'dev' || path.startsWith('dev/')
}
